import type {
  Align,
  Column,
  EmailDocument,
  Padding,
  Row,
  TextBlock,
} from "./types";
import {
  emailRowAreaWidth,
  resolveColumnPixelWidths,
  rowInnerWidth,
} from "./column-presets";
import { findGroup, locateRow } from "./find";
import { ZERO_PADDING } from "./create-shared";

export const BLOCK_WIDTH_MIN_PX = 16;
export const BLOCK_WIDTH_SNAP = 4;

export type BlockLayoutContext = {
  rowAreaWidth: number;
  rowInnerWidth: number;
  columnWidth: number;
  contentMaxWidth: number;
};

export function resolveRowLayoutWidth(
  document: EmailDocument,
  rowId: string,
): { rowAreaWidth: number; innerWidth: number; row: Row } | undefined {
  const located = locateRow(document.sections, rowId);
  if (!located) {
    return undefined;
  }

  const pagePadding = document.settings.pagePadding ?? ZERO_PADDING;
  const rowAreaWidth = emailRowAreaWidth(
    document.settings.contentWidth,
    pagePadding,
  );
  let rowContentWidth = rowAreaWidth;

  if (located.location.parent === 'group') {
    const group = findGroup(document.sections, located.location.groupId);
    if (group) {
      rowContentWidth = rowInnerWidth(
        rowAreaWidth,
        group.padding,
        group.border,
      );
    }
  }

  return {
    rowAreaWidth,
    innerWidth: rowInnerWidth(
      rowContentWidth,
      located.row.padding,
      located.row.border,
    ),
    row: located.row,
  };
}

export function resolveBlockLayoutContext(
  document: EmailDocument,
  rowId: string,
  columnId: string,
  blockPadding: Padding = ZERO_PADDING,
): BlockLayoutContext {
  const pagePadding = document.settings.pagePadding ?? ZERO_PADDING;
  const rowAreaWidth = emailRowAreaWidth(
    document.settings.contentWidth,
    pagePadding,
  );
  const layout = resolveRowLayoutWidth(document, rowId);

  if (!layout) {
    return {
      rowAreaWidth,
      rowInnerWidth: rowAreaWidth,
      columnWidth: rowAreaWidth,
      contentMaxWidth: blockContentMaxWidth(rowAreaWidth, blockPadding),
    };
  }

  const columnIndex = layout.row.columns.findIndex(
    column => column.id === columnId,
  );
  const column = columnIndex >= 0 ? layout.row.columns[columnIndex] : undefined;
  const resolvedColumnWidths = resolveColumnPixelWidths(
    layout.row.columnWidths,
    layout.innerWidth,
    layout.row.columnGap,
  );
  const rawColumnWidth =
    columnIndex >= 0
      ? (resolvedColumnWidths[columnIndex] ?? layout.innerWidth)
      : layout.innerWidth;
  const columnWidth = column
    ? Math.max(0, rawColumnWidth - column.padding.left - column.padding.right)
    : rawColumnWidth;

  return {
    rowAreaWidth: layout.rowAreaWidth,
    rowInnerWidth: layout.innerWidth,
    columnWidth,
    contentMaxWidth: blockContentMaxWidth(columnWidth, blockPadding),
  };
}

export function blockContentMaxWidth(
  columnWidth: number,
  padding: Padding,
): number {
  return Math.max(0, columnWidth - padding.left - padding.right);
}

export function clampBlockWidthPx(px: number, max: number): number {
  return Math.min(max, Math.max(BLOCK_WIDTH_MIN_PX, px));
}

export function snapBlockWidthPx(
  px: number,
  max: number,
  fine: boolean,
): number {
  const step = fine ? 1 : BLOCK_WIDTH_SNAP;
  return clampBlockWidthPx(Math.round(px / step) * step, max);
}

export function effectiveTextBlockWidthPx(
  block: TextBlock,
  contentMaxWidth: number,
): number | null {
  if (block.widthPx == null) {
    return null;
  }
  return clampBlockWidthPx(block.widthPx, contentMaxWidth);
}

export function textBlockStartWidthPx(
  block: TextBlock,
  contentMaxWidth: number,
): number {
  return effectiveTextBlockWidthPx(block, contentMaxWidth) ?? contentMaxWidth;
}

/** Full column width is stored as auto (null); narrower widths are fixed. */
export function resolveTextBlockWidthCommit(
  widthPx: number,
  contentMaxWidth: number,
): number | null {
  return widthPx >= contentMaxWidth ? null : widthPx;
}

function columnContentWidthPx(
  columnPixelWidth: number,
  column: Column,
): number {
  return Math.max(
    0,
    columnPixelWidth - column.padding.left - column.padding.right,
  );
}

/** Reset text blocks to full width when the column is narrower than their max width. */
export function releaseTextBlockWidthsForRow(
  row: Row,
  innerWidth: number,
): boolean {
  const pixelWidths = resolveColumnPixelWidths(
    row.columnWidths,
    innerWidth,
    row.columnGap,
  );
  let changed = false;

  row.columns.forEach((column, columnIndex) => {
    const columnContentWidth = columnContentWidthPx(
      pixelWidths[columnIndex] ?? 0,
      column,
    );

    for (const block of column.blocks) {
      if (block.type !== "text" || block.widthPx == null) {
        continue;
      }

      const contentMaxWidth = blockContentMaxWidth(
        columnContentWidth,
        block.padding,
      );
      if (resolveTextBlockWidthCommit(block.widthPx, contentMaxWidth) === null) {
        block.widthPx = null;
        changed = true;
      }
    }
  });

  return changed;
}

export function blockWidthAlignMargins(align: Align): {
  marginLeft: number | 'auto';
  marginRight: number | 'auto';
} {
  return {
    marginLeft: align === 'left' ? 0 : 'auto',
    marginRight: align === 'right' ? 0 : 'auto',
  };
}

export function textWidthGuideLineLeft(
  align: Align,
  padding: Padding,
  widthPx: number,
  contentMaxWidth: number,
): number {
  if (align === 'left') {
    return padding.left + widthPx;
  }
  if (align === 'right') {
    return padding.left + contentMaxWidth;
  }
  return padding.left + (contentMaxWidth + widthPx) / 2;
}

/** Right-edge drag: center-aligned boxes grow symmetrically so width changes twice as fast as the edge moves. */
export function resolveTextWidthResizePointerScale(
  align: Align,
  fromCenter: boolean,
): number {
  if (fromCenter || align === 'center') {
    return 2;
  }
  return 1;
}

export function imageBoxWidthPx(
  widthPct: number,
  contentMaxWidth: number,
): number {
  if (contentMaxWidth <= 0) {
    return 0;
  }
  return Math.round((widthPct / 100) * contentMaxWidth);
}

export { isFineResizeStep } from "./image-height";
